import { useSyncExternalStore } from "react";

export type FormFactor = "desktop" | "mobile";

/** Viewport width (px) at and above which the desktop shell renders. */
export const DESKTOP_MIN_WIDTH = 1024;

const DESKTOP_MEDIA_QUERY = `(min-width: ${DESKTOP_MIN_WIDTH}px)`;

function isMobileBuildTarget(): boolean {
  return import.meta.env.VITE_BUILD_TARGET === "mobile";
}

/**
 * Pure form-factor read. The mobile build target always wins so a phone
 * bundle never falls back to the desktop shell on a wide viewport. With no
 * `matchMedia` (non-browser context) we default to desktop.
 */
export function readFormFactor(): FormFactor {
  if (isMobileBuildTarget()) return "mobile";
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return "desktop";
  }
  return window.matchMedia(DESKTOP_MEDIA_QUERY).matches ? "desktop" : "mobile";
}

function subscribe(onStoreChange: () => void): () => void {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return () => {};
  }
  const query = window.matchMedia(DESKTOP_MEDIA_QUERY);
  query.addEventListener("change", onStoreChange);
  return () => query.removeEventListener("change", onStoreChange);
}

export function useFormFactor(): FormFactor {
  return useSyncExternalStore(subscribe, readFormFactor, readFormFactor);
}

/** Width (px) from which the mobile shell has room for iPad-style layouts:
 *  split panes and centred form sheets instead of full-width sheets. Every
 *  phone in portrait and landscape stays below it; every iPad is at or
 *  above it (iPad mini portrait is 744). */
export const REGULAR_WIDTH_MIN = 700;

const REGULAR_WIDTH_QUERY = `(min-width: ${REGULAR_WIDTH_MIN}px)`;

function readRegularWidth(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(REGULAR_WIDTH_QUERY).matches;
}

function subscribeRegularWidth(onStoreChange: () => void): () => void {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return () => {};
  }
  const query = window.matchMedia(REGULAR_WIDTH_QUERY);
  query.addEventListener("change", onStoreChange);
  return () => query.removeEventListener("change", onStoreChange);
}

/** `true` on tablet-width viewports (iOS "regular" horizontal size class). */
export function useRegularWidth(): boolean {
  return useSyncExternalStore(subscribeRegularWidth, readRegularWidth, readRegularWidth);
}
